import { mainLayoutConstants } from '@/lib/main-layout-constants'
import { useNav } from '@/provider/NavProvider'
import useStore from '@/store/useStore'
import { Link, navigation } from '@local/ui'
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeftOutlined'
import { Box, Divider, IconButton, List, ListSubheader, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React, { useRef } from 'react'
import { useObservable } from 'react-use'
import { useMainLayoutContext } from '../../MainLayoutContext'
import LeftBottomProfile from './components/LeftBottomProfile'
import SectionMenu from './components/SectionMenu'
import SideMenuItem from './components/SideMenuItem'
import { rootSx } from './style'

const { appbarHeight, sidemenu } = mainLayoutConstants

const EMPTY_MENU_MANAGER = new navigation.MenuManager([])

function Sidebar() {
  const { sceneManager } = useMainLayoutContext()
  const { mainLayoutStore } = useStore()

  const scene = useObservable(sceneManager.observeCurrentSceneOrNull(), null)
  const currentUrl = scene?.url

  const controlKeyDownRef = useRef(false)

  React.useEffect(() => {
    const handleDown = (e: KeyboardEvent) => {
      // console.log(e)
      controlKeyDownRef.current = e.ctrlKey && !e.altKey && !e.shiftKey
    }

    window.addEventListener('keydown', handleDown)
    window.addEventListener('keyup', handleDown)

    return () => {
      window.removeEventListener('keydown', handleDown)
      window.removeEventListener('keyup', handleDown)
    }
  }, [])

  const navManager = useNav()
  const menuManager = useObservable(navManager.observeMenuManager(), EMPTY_MENU_MANAGER)
  const menuItems = menuManager?.menuItems ?? []

  // 링크이동
  const handleClickMenu = (menu: navigation.IMenu) => (e: React.MouseEvent) => {
    e.preventDefault()
    const { href, sectionId } = menu
    if (sectionId) {
      mainLayoutStore.expandSection(sectionId)
    }

    sceneManager.addScene(menu.href)
  }

  // 섹션 클릭
  const handleClickSection = (section: navigation.ISection) => (_e: React.MouseEvent) => {
    mainLayoutStore.toggleExpandSection(section.sectionId)
  }

  return (
    <Box sx={rootSx} className="Sidebar-root">
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          height: appbarHeight,
          width: '100%',
          pl: 2,
          pr: 1,
        }}
      >
        <Link
          href="/"
          sx={{
            flex: 1,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            color: '#000',
            '&:hover': {
              color: (theme) => theme.palette.primary.dark,
            },
            '&:hover .MuiSvgIcon-root': {
              color: (theme) => theme.palette.primary.dark,
            },
          }}
        >
          <img
            src={'/images/miniadms.png'}
            srcSet={'/images/miniadms.png'}
            alt={'CLOVER 로코'}
            loading="lazy"
            style={{ width: '100%', height: '50px' }}
          />
        </Link>
        <IconButton onClick={() => mainLayoutStore.toggleOpen()} sx={{ ml: 'auto', color: '#666' }}>
          <ArrowBackIcon />
        </IconButton>
      </Stack>
      <Divider color={sidemenu.dividerColor} />
      <Box flex={1} sx={{ overflow: 'auto' }} className="Sidebar-body">
        <List component="nav">
          {menuItems.map((item) => {
            if (item.type === 'menu') {
              return (
                <SideMenuItem
                  className="Sidebar-sideMenuItem"
                  key={item.id}
                  icon={item.icon}
                  title={item.title}
                  active={menuManager.isMenuPathMatched(item, currentUrl)}
                  onClick={handleClickMenu(item)}
                  depth1
                />
              )
            }
            if (item.type === 'divider') {
              return <Divider key={item.id} color={sidemenu.dividerColor} />
            }
            if (item.type === 'label') {
              return (
                <ListSubheader key={item.id} className="Sidebar-label">
                  {item.title}
                </ListSubheader>
              )
            }
            if (item.type === 'section') {
              const section = item as navigation.ISection
              return (
                <SectionMenu
                  key={item.id}
                  section={section}
                  menuManager={menuManager}
                  currentHref={currentUrl}
                  expanded={mainLayoutStore.expandedSectionIds.includes(section.sectionId)}
                  onClickMenu={handleClickMenu}
                  onClickSection={handleClickSection(section)}
                />
              )
            }
            return <div key={item}>{JSON.stringify(item)}</div>
          })}
        </List>
      </Box>
      <LeftBottomProfile />
    </Box>
  )
}

export default observer(Sidebar)
